import { requestIdleCallback } from '@theme/utilities';

class PdpMediaPreloader extends HTMLElement {
  connectedCallback() {
    if (this.dataset.preloaded === 'true') return;

    let entries = [];
    try {
      const json = this.querySelector('script[type="application/json"]')?.textContent ?? '[]';
      entries = JSON.parse(json);
    } catch {
      return;
    }

    if (!Array.isArray(entries) || entries.length === 0) return;

    const begin = () => requestIdleCallback(() => this.#preload(entries));
    if (document.readyState === 'complete') {
      begin();
    } else {
      window.addEventListener('load', begin, { once: true });
    }
  }

  /**
   * Warm the cache one image at a time, yielding to idle time between each so the network is never
   * saturated.
   * @param {{ srcset?: string, sizes?: string }[]} entries
   */
  #preload(entries) {
    this.dataset.preloaded = 'true';
    let index = 0;

    const loadNext = () => {
      if (index >= entries.length) return;

      const { srcset, src, sizes } = entries[index];
      index += 1;

      if (!srcset && !src) {
        requestIdleCallback(loadNext);
        return;
      }

      const image = new Image();
      image.decoding = 'async';
      // Low priority: warming the cache must never compete with user-initiated requests.
      image.fetchPriority = 'low';
      if (sizes) image.sizes = sizes;
      // Advance once this image settles, staying within idle time.
      const advance = () => requestIdleCallback(loadNext);
      image.onload = advance;
      image.onerror = advance;
      // Plain fallback first, then srcset last so candidate selection runs with `sizes` applied.
      // When srcset is present the browser fetches the selected candidate (not src); src is the
      // fallback for the rare no-srcset case.
      if (src) image.src = src;
      if (srcset) image.srcset = srcset;
    };

    loadNext();
  }
}

if (!customElements.get('pdp-media-preloader')) {
  customElements.define('pdp-media-preloader', PdpMediaPreloader);
}
